import { useClientPermissionAccess } from '@/hooks/use-client-permission-access';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { createFileRoute } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useGetServiceCategories } from '@/api/generated/service-categories/service-categories';
import { useGetServices } from '@/api/generated/services/services';
import { useGetStaff } from '@/api/generated/staff/staff';
import { useGetTags } from '@/api/generated/tags/tags';
import { useCanManageSalonAdminEquivalent, useCanWriteOperational, useOrg } from '@/providers';
import { PageCanvas } from '@/components/layout/page-canvas';
import {
  AddClientDrawer,
  buildClientExportParams,
  ClientDetailDrawer,
  ClientOptionsDrawer,
  createDefaultClientOptionsFilters,
  downloadClientsCsv,
  isDefaultClientOptionsFilters,
  normalizeClientOptionsFilters,
  type ClientOptionsFilters,
  type ClientOptionsLookupItem,
  type ClientOptionsMetadata,
} from '@/features/clients';
import {
  ClientDirectoryToolbar,
  type ClientStageFilter,
} from '@/features/clients/components/client-directory-toolbar';
import type { ClientQuickFilter } from '@/features/clients/components/client-quick-filter-options';
import {
  ClientListLoadMoreStatus,
  ClientListVirtualBody,
} from '@/features/clients/components/client-list-virtual-body';
import { ClientDetailDrawerById } from '@/features/clients/components/client-detail-drawer-by-id';
import { usePrefetchClientDetail } from '@/features/clients/hooks/client-detail-query';
import { ClientGoHighLevelFilterChips } from '@/features/clients/components/client-gohighlevel-filter-chips';
import { ClientImportHistoryDialog } from '@/features/clients/client-import-shared/client-import-history-dialog';
import { useClientImportNavigationGuard } from '@/features/clients/client-import-shared/use-client-import-navigation-guard';
import { GoHighLevelImportDialog } from '@/features/clients/gohighlevel-import/gohighlevel-import-dialog';
import { PosImportDialog } from '@/features/clients/pos-import/pos-import-dialog';
import {
  useApplyImportBatchSearch,
  useClientsGoHighLevelMetadata,
  useHasClientImports,
  useImportBatchFileName,
  validateClientImportBatchSearch,
} from '@/features/clients/hooks/use-clients-gohighlevel-filters';
import {
  useApplyClientQuickFilterSearch,
  validateClientQuickFilterSearch,
} from '@/features/clients/hooks/use-clients-quick-filter-search';
import {
  clientListColumnSet,
  CLIENT_LIST_TABLE_MEDIA_QUERY,
  type ClientListVariant,
} from '@/features/clients/components/client-list-columns';
import { ClientListSkeleton } from '@/features/clients/components/client-list-skeleton';
import { ClientsListHeader } from '@/features/clients/components/clients-list-header';
import { useClientListSort } from '@/features/clients/hooks/use-client-list-sort';
import {
  buildClientDirectoryListParams,
  lifecycleStagesFor,
  useClientListInfiniteQuery,
} from '@/features/clients/hooks/use-client-list-infinite-query';
import { useClientListSelectAll } from '@/features/clients/hooks/use-client-list-select-all';
import { ActiveBulkSendPill } from '@/features/clients/text-clients/active-bulk-send-pill';
import { ClientsSelectAllCheckbox } from '@/features/clients/text-clients/clients-select-all-checkbox';
import { ClientsSelectionBar } from '@/features/clients/text-clients/clients-selection-bar';
import { TextClientsFocusLayer } from '@/features/clients/text-clients/text-clients-focus-view';
import { useActiveBulkSend, useViewBulkSend } from '@/features/clients/text-clients/use-active-bulk-send';
import { useClientsBulkSmsDraft } from '@/features/clients/text-clients/use-clients-bulk-sms-draft';
import { useHasPlanFeature } from '@/hooks/use-plan-access';
import { useClientsRowSelection } from '@/features/clients/text-clients/use-clients-row-selection';
import { validateTextClientsSearch } from '@/features/clients/text-clients/use-text-clients-focus-view-search';
import { useTextSentToast } from '@/features/clients/text-clients/use-text-sent-toast';
import type { BulkSmsDetail } from '@/features/marketing/bulk-sms/bulk-sms-types';
import { useMarketingAccess } from '@/features/marketing/use-marketing-access';
import type { Client } from '@/features/clients/components/client-detail-drawer-types';
import { useDebounce } from '@/hooks/use-debounce';
import { useMediaQuery } from '@/hooks/use-media-query';
import { isClientListSettled } from '@/features/clients/lib/client-list-settled';
import { useNow } from '@/hooks/use-now';
import { salonDateKey } from '@/lib/salon-date-anchor';
import { useSalonTimezone } from '@/hooks/use-salon-timezone';
import { useSalonSetup } from '@/hooks/use-salon-setup';
import { clientsListFootnote } from '@/features/clients/components/clients-visits-note';
import { salonSetupMeanings } from '@/features/salon-setup/use-salon-setup-meanings';
import { ClientsEmptyListMessage } from '@/features/clients/components/clients-empty-list-message';
import {
  allowedClientQuickFilter,
  clientsOpenOnAllChip,
  defaultClientStageFilter,
  useVisitHistoryClientsListDefaults,
} from '@/features/clients/hooks/use-visit-history-clients-list-defaults';
import { formatDocumentTitle } from '@/lib/document-title';
import { refiningListClass } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { MAX_BULK_RECIPIENTS, isVisitHistoryQuickFilter } from '@repo/shared/marketing';

export const Route = createFileRoute('/_auth/$orgId/clients')({
  // `?text=<bulkSendId>` opens the Text focus view over the list (see TextClientsFocusLayer);
  // `?importBatchId=<id>` opens the list on one import's people (see useApplyImportBatchSearch);
  // `?quickFilter=<id>` opens it on one Quick filter (see useApplyClientQuickFilterSearch). An import's people win
  // over a Quick filter: both at once keeps only the import (dropped here, so it can't replace that view later).
  validateSearch: (search: Record<string, unknown>) => {
    const importBatch = validateClientImportBatchSearch(search);
    return {
      ...validateTextClientsSearch(search),
      ...importBatch,
      ...(importBatch.importBatchId ? {} : validateClientQuickFilterSearch(search)),
    };
  },
  component: ClientsPage,
});

function getClientName(client: Pick<Client, 'firstName' | 'lastName'>) {
  return [client.firstName, client.lastName].filter(Boolean).join(' ');
}

function notifyPickLimitReached() {
  toast(`You can pick up to ${MAX_BULK_RECIPIENTS.toLocaleString('en-US')} people for one text.`, {
    id: 'clients-pick-limit',
  });
}

function ClientsPage() {
  const access = useClientPermissionAccess();
  if (!access.canAccessDirectory)
    return (
      <div role="status" className="p-6">
        You do not have access to the Clients directory.
      </div>
    );
  return <ClientDirectory key={access.scopeKey} />;
}

function ClientDirectory() {
  const access = useClientPermissionAccess();
  const { orgId } = useOrg();
  // The URL's `$orgId` segment (short code) — what route links take; `orgId` above is the real org id.
  const { orgId: orgRouteParam } = Route.useParams();
  const {
    text: textBulkSendId,
    importBatchId: importBatchIdSearch,
    quickFilter: quickFilterSearch,
  } = Route.useSearch();
  const canWriteOperational = useCanWriteOperational();
  const marketingAccess = useMarketingAccess();
  // Starting a bulk message needs marketing edit access and a plan with bulk texting (the API's own rules for a draft).
  const hasBulkSms = useHasPlanFeature('BULK_SMS');
  const canTextClients = marketingAccess.canEdit && hasBulkSms;
  // The salon's one running send (Leads or Clients), for whoever may see bulk sends: the toolbar shows it as a
  // pill, and the selection bar waits for it.
  const activeSend = useActiveBulkSend(orgRouteParam, marketingAccess.canReadBulk);
  const viewBulkSend = useViewBulkSend(orgRouteParam);
  // The selection bar's draft starter — owned here so it outlives the bar (see useClientsBulkSmsDraft).
  const textDraft = useClientsBulkSmsDraft(orgRouteParam);
  // Ticked people (a basket that survives filter and search changes) — only for people who can text; on hold
  // while a draft is being made, so the draft and the bar always agree on who is picked.
  const selection = useClientsRowSelection({
    enabled: canTextClients,
    locked: textDraft.isPending,
    onLimitReached: notifyPickLimitReached,
  });
  const selecting = selection.count > 0;
  const [selectionBarHeight, setSelectionBarHeight] = useState(0);
  const { clear: clearPicked } = selection;
  // After Send: the ticks are done with, and closing the view says the text is on its way.
  const markTextSent = useTextSentToast(orgRouteParam, textBulkSendId);
  const handleSent = useCallback(
    (sent: BulkSmsDetail) => {
      clearPicked();
      markTextSent(sent);
    },
    [clearPicked, markTextSent]
  );
  // CSV export of the whole client list is owner/agency only (the API refuses everyone else).
  const canExportClients = useCanManageSalonAdminEquivalent();
  const queryClient = useQueryClient();
  // Hovering or focusing a row starts its profile read, so the drawer usually opens on data.
  const prefetchClientDetail = usePrefetchClientDetail();
  const prefetchClientRow = useCallback((client: Client) => prefetchClientDetail(client.id), [prefetchClientDetail]);
  useEffect(
    () => () => {
      const queryKey = ['clients', orgId, access.scopeKey];
      void queryClient.cancelQueries({ queryKey });
      queryClient.removeQueries({ queryKey });
    },
    [queryClient, orgId, access.scopeKey]
  );
  const [searchQuery, setSearchQuery] = useState('');
  // Arriving on "See these people" (`?importBatchId=`) starts on that import's people right away (no first fetch
  // of the default list); useApplyImportBatchSearch below then strips the URL and handles later arrivals.
  // Arriving on a Quick filter link (`?quickFilter=`) starts on that filter the same way; clearing it later puts back
  // the default Clients chip.
  // Check-in off: the page opens on "All" (imports stay Leads). Bookings on the salon's POS or visits that come only
  // from Hayven Tap change what the rows and notes say; with no visit history kept the page never keeps a
  // visit-history Quick filter.
  const setupState = useSalonSetup();
  const { isResolved: setupResolved, isError: setupFailed } = setupState;
  const { bookingsOnPos, visitsByTap, noVisitHistory, deskMarksDone } = salonSetupMeanings(setupState);
  const visitsNote = clientsListFootnote({ bookingsOnPos, visitsByTap, finishesTurns: deskMarksDone });
  const opensOnAll = clientsOpenOnAllChip(setupState);
  const defaultStageFilter = defaultClientStageFilter(opensOnAll);
  const initialQuickFilter = allowedClientQuickFilter(quickFilterSearch, noVisitHistory);
  const [stageFilter, setStageFilter] = useState<ClientStageFilter>(() =>
    importBatchIdSearch || initialQuickFilter ? 'all' : defaultStageFilter
  );
  const [quickFilter, setQuickFilter] = useState<ClientQuickFilter | null>(() => initialQuickFilter);
  // The stage chip in use before a Quick filter switched it to "All" — restored when the filter is cleared.
  const stageBeforeQuickFilterRef = useRef<ClientStageFilter | null>(initialQuickFilter ? defaultStageFilter : null);
  const [isAddClientOpen, setIsAddClientOpen] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isGoHighLevelImportOpen, setIsGoHighLevelImportOpen] = useState(false);
  const [isPosImportOpen, setIsPosImportOpen] = useState(false);
  const [isImportHistoryOpen, setIsImportHistoryOpen] = useState(false);
  const clientImportGuard = useClientImportNavigationGuard();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  // A client opened by id only (a phone-sharing household member, a split-off profile).
  const [openClientId, setOpenClientId] = useState<string | null>(null);
  const openClientById = (clientId: string) => {
    setSelectedClient(null);
    setOpenClientId(clientId);
  };
  const [activeFilters, setActiveFilters] = useState<ClientOptionsFilters>(() => ({
    ...createDefaultClientOptionsFilters(),
    importBatchId: importBatchIdSearch ?? '',
  }));
  const [draftFilters, setDraftFilters] = useState<ClientOptionsFilters>(() => activeFilters);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(() => Boolean(importBatchIdSearch));
  const [knownClientLookups, setKnownClientLookups] = useState<ClientOptionsLookupItem[]>([]);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const staffQuery = useGetStaff(
    { limit: '100', isActive: 'true' },
    { query: { enabled: !!orgId, staleTime: 5 * 60 * 1000 } }
  );
  const servicesQuery = useGetServices(
    { includeCategory: 'true' },
    { query: { enabled: !!orgId, staleTime: 5 * 60 * 1000 } }
  );
  const serviceCategoriesQuery = useGetServiceCategories(
    { includeServices: 'true' },
    { query: { enabled: !!orgId, staleTime: 5 * 60 * 1000 } }
  );
  const tagsQuery = useGetTags(undefined, {
    query: { enabled: !!orgId, staleTime: 5 * 60 * 1000 },
  });
  // GoHighLevel filters exist only for salons that imported from it (the drawer hides them otherwise).
  const goHighLevel = useClientsGoHighLevelMetadata(!!orgId);
  const importFileName = useImportBatchFileName(activeFilters.importBatchId, canExportClients);

  // Date filters ("Today", "This Week"…) cover salon days, whatever the viewer's browser timezone.
  const { timezone: salonTimezone } = useSalonTimezone();

  const requestMetadata = useMemo<ClientOptionsMetadata>(
    () => ({
      staff: (staffQuery.data?.data.items ?? []).map((staff) => ({
        id: staff.id,
        name: getClientName(staff),
      })),
      tags: (tagsQuery.data?.data.items ?? []).map((tag) => ({
        id: tag.id,
        name: tag.name,
      })),
      clients: knownClientLookups,
    }),
    [knownClientLookups, staffQuery.data?.data.items, tagsQuery.data?.data.items]
  );

  // 'clients' = everyone past the LEAD stage; 'leads' = campaign sign-ups not yet booked.
  const lifecycleStagesParam = lifecycleStagesFor(stageFilter);

  // The Leads chip swaps the table to lead columns (Came from, Signed up); Clients and All share the rest.
  const listVariant: ClientListVariant = stageFilter === 'leads' ? 'leads' : 'clients';
  // The header's column sort (default: latest activity first, newest sign-ups first on Leads); the API orders the
  // whole list by it and pages it.
  const { sort, toggle: toggleSort } = useClientListSort(listVariant, bookingsOnPos);

  const listParams = useMemo(
    () =>
      buildClientDirectoryListParams({
        filters: activeFilters,
        search: debouncedSearchQuery,
        metadata: requestMetadata,
        timezone: salonTimezone,
        stageFilter,
        quickFilter,
        sort,
      }),
    [activeFilters, debouncedSearchQuery, quickFilter, requestMetadata, salonTimezone, sort, stageFilter]
  );

  // A Quick filter spans clients AND leads (its menu count is over everyone), so picking one shows "All";
  // the stage chips still narrow it afterwards. Clearing it puts back the chip it replaced, unless staff
  // picked a chip themselves in the meantime.
  const handleQuickFilterChange = useCallback(
    (next: ClientQuickFilter | null) => {
      if (next) {
        if (!quickFilter) stageBeforeQuickFilterRef.current = stageFilter;
        setStageFilter('all');
      } else {
        if (stageBeforeQuickFilterRef.current) setStageFilter(stageBeforeQuickFilterRef.current);
        stageBeforeQuickFilterRef.current = null;
      }
      setQuickFilter(next);
    },
    [quickFilter, stageFilter]
  );

  const clearQuickFilter = useCallback(() => handleQuickFilterChange(null), [handleQuickFilterChange]);
  const markStagePicked = useVisitHistoryClientsListDefaults({
    opensOnAll,
    noVisitHistory,
    isResolved: setupResolved,
    stageFilter,
    quickFilter,
    setStageFilter,
    clearQuickFilter,
    stageBeforeQuickFilterRef,
  });

  const handleStageFilterChange = useCallback(
    (next: ClientStageFilter) => {
      markStagePicked();
      stageBeforeQuickFilterRef.current = null;
      setStageFilter(next);
    },
    [markStagePicked]
  );

  // Rows render ONE layout: table columns from `sm` up, a stacked summary on phones.
  const isTableLayout = useMediaQuery(CLIENT_LIST_TABLE_MEDIA_QUERY);

  // Where focus lands when the selection bar (or the Text view opened from it) goes away, never <body>: the
  // header's select-all (table layout only — phones have no header), else the Sending pill, else the search box.
  const selectAllRef = useRef<HTMLInputElement>(null);
  const sendPillRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const getFocusAfterSelection = useCallback(() => {
    const selectAll = isTableLayout ? selectAllRef.current : null;
    if (selectAll && !selectAll.disabled) return selectAll;
    return sendPillRef.current ?? searchInputRef.current;
  }, [isTableLayout]);
  const handleClearPicked = useCallback(() => {
    // Commit now so focus moves after the bar is gone (Clear itself unmounts with it).
    flushSync(clearPicked);
    getFocusAfterSelection()?.focus();
  }, [clearPicked, getFocusAfterSelection]);

  const {
    query: {
      isPending,
      isPlaceholderData: isRefining,
      error,
      hasNextPage,
      isFetchingNextPage,
      isFetchNextPageError,
      fetchNextPage,
    },
    clients,
    pageCount,
    totalCount,
    leadCount,
  } = useClientListInfiniteQuery(orgId, access.scopeKey, listParams, {
    // A visit-history Quick filter from a link waits for the salon's setup (when no visit history is kept it is
    // dropped instead); a failed settings read lets it through (the API still refuses it then).
    enabled: setupResolved || setupFailed || !quickFilter || !isVisitHistoryQuickFilter(quickFilter),
  });
  const realClientCount = totalCount - leadCount;
  const loadNextPage = useCallback(() => void fetchNextPage(), [fetchNextPage]);
  const listKey = useMemo(() => JSON.stringify(listParams), [listParams]);
  // One page clock for every row's "12 min ago" / "Today" (rows are memoized, so they'd otherwise freeze);
  // rows that only read salon days get a clock that moves once a day (`clientRowClock`).
  const now = useNow(60_000);
  const salonToday = salonDateKey(now, salonTimezone);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-created only when the salon day changes
  const dayNow = useMemo(() => now, [salonToday]);
  const listSettled = isClientListSettled({
    isPending,
    hasError: !!error,
    isPlaceholderData: isRefining,
    searchSettled: searchQuery === debouncedSearchQuery,
  });

  // Select-all ticks everyone the list matches, read by id when not every page is loaded yet.
  const selectAll = useClientListSelectAll({
    orgId,
    scopeKey: access.scopeKey,
    params: listParams,
    loadedClients: clients,
    fullyLoaded: !hasNextPage && !isRefining,
    trackState: canTextClients && selecting && listSettled,
  });
  const { toggleVisible } = selection;
  const handleSelectAll = useCallback(() => {
    // In table order, so a pick capped at the recipient limit takes the rows staff see first.
    selectAll.selectAll(toggleVisible).then(
      (applied) => {
        if (!applied) toast('The list changed while it was loading. Select all again to pick this list.');
      },
      () => toast.error('Could not select everyone. Please try again.')
    );
  }, [selectAll, toggleVisible]);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (clients.length === 0) return;

    setKnownClientLookups((current) => {
      const nextById = new Map(current.map((client) => [client.id, client.name]));
      let hasChanges = false;

      for (const client of clients) {
        const name = getClientName(client);
        if (nextById.get(client.id) === name) continue;
        nextById.set(client.id, name);
        hasChanges = true;
      }

      if (!hasChanges) return current;
      return Array.from(nextById, ([id, name]) => ({ id, name }));
    });
  }, [clients]);

  const metadata = useMemo<ClientOptionsMetadata>(
    () => ({
      ...requestMetadata,
      services: (servicesQuery.data?.data ?? []).map((service) => ({
        id: service.id,
        name: service.name,
        defaultPrice: service.defaultPrice,
        categoryId: service.categoryId,
        categoryName: service.category?.name ?? null,
        isActive: service.isActive,
      })),
      serviceCategories: (serviceCategoriesQuery.data?.data ?? []).map((category) => ({
        id: category.id,
        name: category.name,
      })),
      goHighLevel,
    }),
    [goHighLevel, requestMetadata, serviceCategoriesQuery.data?.data, servicesQuery.data?.data]
  );

  const handleOptionsClick = useCallback(() => {
    setDraftFilters(activeFilters);
    setIsOptionsOpen(true);
  }, [activeFilters]);

  const handleApplyOptions = useCallback(() => {
    const nextFilters = normalizeClientOptionsFilters(draftFilters);
    setActiveFilters(nextFilters);
    setDraftFilters(nextFilters);
    setHasAppliedFilters(!isDefaultClientOptionsFilters(nextFilters));
    setIsOptionsOpen(false);
  }, [draftFilters]);

  // A removed filter chip patches the applied filters (and the drawer's draft, so reopening it agrees).
  const handleFilterChipChange = useCallback(
    (patch: Partial<ClientOptionsFilters>) => {
      const nextFilters = normalizeClientOptionsFilters({ ...activeFilters, ...patch });
      setActiveFilters(nextFilters);
      setDraftFilters(nextFilters);
      setHasAppliedFilters(!isDefaultClientOptionsFilters(nextFilters));
    },
    [activeFilters]
  );

  // "See these people" after an import: exactly that import's people, whatever stage they are in — every other
  // filter, the search and the Quick filter are cleared so the count matches the import's.
  const applyImportBatch = useCallback((importBatchId: string) => {
    const nextFilters = { ...createDefaultClientOptionsFilters(), importBatchId };
    setActiveFilters(nextFilters);
    setDraftFilters(nextFilters);
    setHasAppliedFilters(true);
    setSearchQuery('');
    setQuickFilter(null);
    stageBeforeQuickFilterRef.current = null;
    setStageFilter('all');
    // The Import entry lives in the Filters drawer: never leave it open over the new list.
    setIsOptionsOpen(false);
  }, []);
  useApplyImportBatchSearch(orgRouteParam, importBatchIdSearch, applyImportBatch);

  // A Quick filter link (Reports › Clients): exactly that filter's people, like picking it from a fresh list — every
  // other filter and the search are cleared so the count matches the link's.
  const applyQuickFilterSearch = useCallback((link: ClientQuickFilter) => {
    const next = allowedClientQuickFilter(link, noVisitHistory);
    if (!next) return;
    const defaults = createDefaultClientOptionsFilters();
    setActiveFilters(defaults);
    setDraftFilters(defaults);
    setHasAppliedFilters(false);
    setSearchQuery('');
    stageBeforeQuickFilterRef.current = defaultClientStageFilter(opensOnAll);
    setStageFilter('all');
    setQuickFilter(next);
  }, [noVisitHistory, opensOnAll]);
  useApplyClientQuickFilterSearch(orgRouteParam, quickFilterSearch, applyQuickFilterSearch);
  const openGoHighLevelImport = useCallback(() => {
    setIsOptionsOpen(false);
    setIsGoHighLevelImportOpen(true);
  }, []);
  const openPosImport = useCallback(() => {
    setIsOptionsOpen(false);
    setIsPosImportOpen(true);
  }, []);
  const hasClientImports = useHasClientImports(canExportClients);
  const openImportHistory = useCallback(() => {
    setIsOptionsOpen(false);
    setIsImportHistoryOpen(true);
  }, []);
  const showImportedPeople = useCallback(
    (importBatchId: string) => {
      setIsGoHighLevelImportOpen(false);
      setIsPosImportOpen(false);
      setIsImportHistoryOpen(false);
      applyImportBatch(importBatchId);
    },
    [applyImportBatch]
  );

  const handleResetOptions = useCallback(() => {
    const defaultFilters = createDefaultClientOptionsFilters();
    setActiveFilters(defaultFilters);
    setDraftFilters(defaultFilters);
    setHasAppliedFilters(false);
  }, []);

  const handleDownloadClients = useCallback(async () => {
    const filters = isOptionsOpen ? draftFilters : activeFilters;
    const params = {
      ...buildClientExportParams(
        normalizeClientOptionsFilters(filters),
        searchQuery,
        metadata,
        undefined,
        salonTimezone
      ),
      // Export what the directory currently shows — respect the stage chips and the Quick filter.
      ...(lifecycleStagesParam ? { lifecycleStages: lifecycleStagesParam } : {}),
      ...(quickFilter ? { quickFilter } : {}),
    };

    try {
      await downloadClientsCsv(params);
      toast.success('Client export downloaded');
    } catch {
      toast.error('Failed to download client export');
    }
  }, [
    activeFilters,
    draftFilters,
    isOptionsOpen,
    lifecycleStagesParam,
    metadata,
    quickFilter,
    salonTimezone,
    searchQuery,
  ]);

  return (
    <TextClientsFocusLayer
      orgRouteParam={orgRouteParam}
      bulkSendId={textBulkSendId}
      onSent={handleSent}
      restoreFocusFallback={getFocusAfterSelection}
    >
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Clients')}</title>
      </Helmet>

      <PageCanvas ref={setScrollElement} className="h-full overflow-auto">
        <div className="min-h-full overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface shadow-soft-lg">
          <ClientDirectoryToolbar
            clientCount={realClientCount}
            leadCount={leadCount}
            stageFilter={stageFilter}
            quickFilter={quickFilter}
            searchQuery={searchQuery}
            searching={!isPending && (isRefining || searchQuery !== debouncedSearchQuery)}
            hasAppliedFilters={hasAppliedFilters}
            readOnly={!canWriteOperational}
            searchInputRef={searchInputRef}
            activeSendPill={
              // One send at a time per salon: its status shows here while it runs. Text itself only starts from
              // ticked rows, in the floating bar.
              activeSend ? (
                <ActiveBulkSendPill
                  ref={sendPillRef}
                  send={activeSend}
                  timezone={salonTimezone}
                  onView={() => viewBulkSend(activeSend, { keepPage: selecting })}
                />
              ) : undefined
            }
            onStageFilterChange={handleStageFilterChange}
            onQuickFilterChange={handleQuickFilterChange}
            onSearchChange={setSearchQuery}
            onAddClient={() => setIsAddClientOpen(true)}
            onMoreFilters={handleOptionsClick}
            noVisitHistory={noVisitHistory}
          />

          <ClientGoHighLevelFilterChips
            filters={activeFilters}
            importFileName={importFileName}
            onChange={handleFilterChipChange}
          />

          {/* Keep the last row clear of the floating selection bar. */}
          <div style={selectionBarHeight > 0 ? { paddingBottom: selectionBarHeight + 40 } : undefined}>
            {/* Narrower than the columns need → the table scrolls sideways in its own box (phones stack rows). */}
            <div className="overflow-x-auto">
              <div className={clientListColumnSet(listVariant, bookingsOnPos).minWidthClass}>
                <div
                  role="table"
                  aria-label={listVariant === 'leads' ? 'Leads' : 'Clients'}
                  // Only the rows in view are built: the count tells screen readers how long the list really is.
                  aria-rowcount={isPending || error ? undefined : totalCount + 1}
                >
                  <ClientsListHeader
                    sort={sort}
                    onSort={toggleSort}
                    variant={listVariant}
                    bookingsOnPos={bookingsOnPos}
                    selectAll={
                      canTextClients ? (
                        <ClientsSelectAllCheckbox
                          state={selection.visibleState(selectAll.rows)}
                          rowCount={totalCount}
                          leadCount={leadCount}
                          // Only once the rows on screen are the current list — never the previous filter's.
                          disabled={!listSettled || textDraft.isPending || selectAll.loading}
                          onToggle={handleSelectAll}
                          inputRef={selectAllRef}
                        />
                      ) : undefined
                    }
                  />

                  {!isPending && !error && clients.length > 0 && (
                    <ClientListVirtualBody
                      // A new filter / search / sort is a new list: its paging starts over. A layout switch
                      // (table ↔ phone rows) re-measures every row.
                      key={`${listKey}:${isTableLayout ? 'table' : 'phone'}`}
                      clients={clients}
                      scrollElement={scrollElement}
                      layout={isTableLayout ? 'table' : 'phone'}
                      variant={listVariant}
                      timezone={salonTimezone}
                      now={now}
                      dayNow={dayNow}
                      isPicked={selection.isPicked}
                      onOpen={setSelectedClient}
                      onIntent={prefetchClientRow}
                      onToggle={canTextClients ? selection.toggle : undefined}
                      refining={isRefining}
                      locked={textDraft.isPending}
                      // Never page a previous filter's rows while the new list loads.
                      hasNextPage={hasNextPage && !isRefining}
                      isFetchingNextPage={isFetchingNextPage}
                      loadedPageCount={pageCount}
                      fetchNextPage={loadNextPage}
                      bookingsOnPos={bookingsOnPos}
                      noVisitHistory={noVisitHistory}
                    />
                  )}
                </div>

                <ClientListLoadMoreStatus
                  hasNextPage={hasNextPage && !isRefining}
                  isFetchingNextPage={isFetchingNextPage}
                  failed={isFetchNextPageError}
                  onRetry={loadNextPage}
                />

                {isPending && <ClientListSkeleton variant={listVariant} bookingsOnPos={bookingsOnPos} />}
              </div>
            </div>

            {visitsNote && !isPending && !error && clients.length > 0 && (
              <p className="border-t border-clovie-border-soft px-4 py-3 text-[16px] text-clovie-muted lg:px-5">
                {visitsNote}
              </p>
            )}

            {error && (
              <div className="px-6 py-16 text-center">
                <p className="text-sm font-medium text-clovie-text">Failed to load clients.</p>
                <p className="mt-1 text-sm text-clovie-muted">Please try again.</p>
              </div>
            )}

            {!isPending && !error && clients.length === 0 && (
              <div aria-busy={isRefining} className={cn('px-6 py-16 text-center', refiningListClass(isRefining))}>
                <ClientsEmptyListMessage
                  searchQuery={searchQuery}
                  filtered={Boolean(quickFilter) || hasAppliedFilters}
                  pointToLeads={visitsByTap && stageFilter === 'clients'}
                  onShowLeads={() => handleStageFilterChange('leads')}
                />
              </div>
            )}
          </div>
        </div>
      </PageCanvas>

      {/* A sibling of the focus view and before it, so an open view covers the bar (and focus returns to it). */}
      {selecting && (
        <ClientsSelectionBar
          draft={textDraft}
          clientIds={selection.ids}
          leadCount={selection.leadCount}
          onClear={handleClearPicked}
          onViewActiveSend={activeSend ? () => viewBulkSend(activeSend, { keepPage: true }) : undefined}
          onHeightChange={setSelectionBarHeight}
        />
      )}

      {/* Add Client Drawer */}
      {canWriteOperational && (
        <AddClientDrawer
          open={isAddClientOpen}
          onClose={() => setIsAddClientOpen(false)}
          onSubmit={() => {
            // TODO: Call API to create client
          }}
          onOpenExistingClient={openClientById}
        />
      )}

      <ClientOptionsDrawer
        open={isOptionsOpen}
        draftFilters={draftFilters}
        metadata={metadata}
        timezone={salonTimezone}
        hasAppliedFilters={hasAppliedFilters}
        onDraftChange={setDraftFilters}
        onApply={handleApplyOptions}
        onReset={handleResetOptions}
        onDownload={canExportClients ? handleDownloadClients : undefined}
        onImport={canExportClients ? openGoHighLevelImport : undefined}
        onImportFromPos={canExportClients ? openPosImport : undefined}
        onOpenImportHistory={canExportClients && hasClientImports ? openImportHistory : undefined}
        noVisitHistory={noVisitHistory}
        onClose={() => setIsOptionsOpen(false)}
      />

      {isGoHighLevelImportOpen && canExportClients ? (
        <GoHighLevelImportDialog
          orgId={orgId}
          onClose={() => setIsGoHighLevelImportOpen(false)}
          onSeeImportedPeople={showImportedPeople}
          navigationGuard={clientImportGuard}
        />
      ) : null}

      {isPosImportOpen && canExportClients ? (
        <PosImportDialog
          orgId={orgId}
          onClose={() => setIsPosImportOpen(false)}
          onSeeImportedPeople={showImportedPeople}
          navigationGuard={clientImportGuard}
        />
      ) : null}

      {isImportHistoryOpen && canExportClients ? (
        <ClientImportHistoryDialog
          onClose={() => setIsImportHistoryOpen(false)}
          onSeeImportedPeople={showImportedPeople}
        />
      ) : null}

      {/* Client Detail Drawer */}
      <ClientDetailDrawer
        open={selectedClient !== null}
        onClose={() => setSelectedClient(null)}
        client={selectedClient}
        onClientUpdate={(updatedClient) => setSelectedClient(updatedClient)}
        onOpenClient={openClientById}
      />
      <ClientDetailDrawerById
        clientId={openClientId}
        onClose={() => setOpenClientId(null)}
        onOpenClient={openClientById}
      />
    </TextClientsFocusLayer>
  );
}
